import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { PUBLIC_URL } from './../../const';
import { useAuth } from "./../../AuthContext";

// ===== i18n =====
const dict = {
    en: {
        pageTitle: "Select Your Workspace",
        loading: "Loading workspaces...",
        failedLoad: "Failed to load workspaces. Please try again.",
        unableSelect: "Unable to select workspace. Please try again.",
        choose: "Choose",
        selecting: "Selecting...",
        noWorkspaces: "No workspaces available.",
        logoAlt: "My-Sites-Guard Logo",
    },
    ru: {
        pageTitle: "Выберите рабочее пространство",
        loading: "Загрузка рабочих пространств...",
        failedLoad: "Не удалось загрузить рабочие пространства. Повторите попытку.",
        unableSelect: "Не удалось выбрать рабочее пространство. Повторите попытку.",
        choose: "Выбрать",
        selecting: "Выбираем...",
        noWorkspaces: "Нет доступных рабочих пространств.",
        logoAlt: "Логотип My-Sites-Guard",
    },
    ua: {
        pageTitle: "Оберіть робочий простір",
        loading: "Завантаження робочих просторів...",
        failedLoad: "Не вдалося завантажити робочі простори. Спробуйте ще раз.",
        unableSelect: "Не вдалося обрати робочий простір. Спробуйте ще раз.",
        choose: "Обрати",
        selecting: "Обираємо...",
        noWorkspaces: "Немає доступних робочих просторів.",
        logoAlt: "Логотип My-Sites-Guard",
    },
};

const langToLocale = {
    en: "en-GB",
    ru: "ru-RU",
    ua: "uk-UA",
};

const getInitialLang = () =>
    (typeof window !== 'undefined' && (localStorage.getItem("lang") || "en").toLowerCase()) || "en";

function createI18n(dict, lang) {
    const base = dict[lang] || dict.en;
    const locale = langToLocale[lang] || langToLocale.en;
    const t = (key, params) => {
        const raw = base[key] ?? dict.en[key] ?? key;
        if (!params) return raw;
        return Object.entries(params).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, String(v)), raw);
    };
    return { t, locale, dict: base };
}

// ===== components =====
const WorkspaceSelection = ({ lang: langProp }) => {
    const lang = langProp || getInitialLang();
    const { t } = useMemo(() => createI18n(dict, lang), [lang]);

    const navigate = useNavigate();
    const { getUserWorkspaces, selectWorkspace } = useAuth();

    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectingId, setSelectingId] = useState(null); // показываем "Selecting..." только на конкретной кнопке
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchWorkspaces = async () => {
            try {
                setError('');
                const response = await getUserWorkspaces();
                setWorkspaces(response.workspaces || []);
            } catch {
                setError(t("failedLoad"));
            } finally {
                setLoading(false);
            }
        };
        fetchWorkspaces();
    }, [getUserWorkspaces, t]);

    const handleWorkspaceSelection = async (workspaceId) => {
        try {
            setError('');
            setSelectingId(workspaceId);
            await selectWorkspace(workspaceId);
            navigate('/dashboard');
        } catch {
            setError(t("unableSelect"));
        } finally {
            setSelectingId(null);
        }
    };

    return (
        <div className="px-2 px-sm-3 px-lg-0 w-100">
            <div className="text-center mb-4">
                <h2 className="fw-bold mb-1">{t("pageTitle")}</h2>
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                    {t("loading")}
                </p>
            </div>

            {error && <p className="alert alert-danger text-danger">{error}</p>}

            {loading ? (
                <div className="d-flex justify-content-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{t("loading")}</span>
                    </div>
                </div>
            ) : (
                <div className="row g-3">
                    {workspaces.length > 0 ? (
                        workspaces.map((workspace) => (
                            <div key={workspace.company_id} className="col-12">
                                <div className="card border-0 shadow-sm h-100">
                                    <div className="card-body d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                                        <div className="d-flex align-items-center gap-3">
                                            <div
                                                className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary fw-bold"
                                                style={{ width: 48, height: 48 }}
                                            >
                                                {workspace.company_name?.[0]?.toUpperCase() || "W"}
                                            </div>
                                            <div>
                                                <div className="fw-semibold fs-5 mb-1 text-dark">
                                                    {workspace.company_name}
                                                </div>
                                                <div className="text-muted" style={{ fontSize: "12px" }}>
                                                    ID: {workspace.company_id}
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            className="btn btn-primary px-4"
                                            onClick={() => handleWorkspaceSelection(workspace.company_id)}
                                            disabled={!!selectingId}
                                        >
                                            {selectingId === workspace.company_id ? t("selecting") : t("choose")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-center text-muted">{t("noWorkspaces")}</p>
                    )}
                </div>
            )}
        </div>
    );
};

const ChooseWorkspace = ({ lang: langProp }) => {
    const lang = langProp || getInitialLang();
    const { t } = useMemo(() => createI18n(dict, lang), [lang]);

    return (
        <>
            <div id="main-wrapper">
                <div className="position-relative overflow-hidden radial-gradient min-vh-100 w-100">
                    <div className="position-relative z-index-5">
                        <div className="row">
                            <div className="col-xl-7 col-xxl-8">
                                <a href="https://my-sites-guard.com/" className="text-nowrap logo-img d-block px-4 py-9 w-100">
                                    <img
                                        src="https://my-sites-guard.com/assets/img/home_logo.png"
                                        className="dark-logo"
                                        alt={t("logoAlt")}
                                        style={{ maxHeight: "40px" }}
                                    />
                                </a>
                                <div className="d-none d-xl-flex align-items-center justify-content-center h-n80">
                                    <img
                                        src={`${PUBLIC_URL}/assets/images/backgrounds/login-bg.png`}
                                        alt=""
                                        className="img-fluid"
                                        width="500"
                                    />
                                </div>
                            </div>
                            <div className="col-xl-5 col-xxl-4">
                                <div className="card mb-0 shadow-none rounded-0 min-vh-100 h-100">
                                    <div className="auth-max-width mx-auto d-flex align-items-center w-100 h-100">
                                        <div className="card-body">
                                            <WorkspaceSelection lang={lang} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* оставил ваши скрипты, если они реально нужны странице */}
            <div className="dark-transparent sidebartoggler"></div>
            <script src="./assets/libs/bootstrap/dist/js/bootstrap.bundle.min.js"></script>
            <script src="./assets/libs/simplebar/dist/simplebar.min.js"></script>
            <script src="./assets/js/theme/app.init.js"></script>
            <script src="./assets/js/theme/theme.js"></script>
            <script src="./assets/js/theme/app.min.js"></script>
            <script src="./assets/js/theme/sidebarmenu.js"></script>
            <script src="https://cdn.jsdelivr.net/npm/iconify-icon@1.0.8/dist/iconify-icon.min.js"></script>
        </>
    );
};

export default ChooseWorkspace;
